import { useState, useEffect } from 'react'
import { 
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer 
} from 'recharts'
import { Plus, Trash2, Activity, Ruler, Weight } from 'lucide-react'
import { format } from 'date-fns'
import { fr } from 'date-fns/locale'
import toast from 'react-hot-toast'
import api from '../../../services/api'

export default function PatientMeasurementsTab({ patientId, patientGender, patientAge }) {
    const [measures, setMeasures] = useState([])
    const [loading, setLoading] = useState(true)
    const [isModalOpen, setIsModalOpen] = useState(false)
    
    const [formData, setFormData] = useState({
        date: new Date().toISOString().split('T')[0],
        weight: '',
        height: '',
        waist: '',
        hips: '', // <-- NOUVEAU
        fat_mass: '',
        muscle_mass: ''
    })

    useEffect(() => {
        fetchMeasures()
    }, [patientId])

    const fetchMeasures = async () => {
        try {
            const res = await api.get(`/api/measurements/patient/${patientId}`)
            setMeasures(res.data)
            
            // Pré-remplissage de la taille
            if (res.data.length > 0) {
                const last = res.data[res.data.length - 1]
                if (last.height) setFormData(prev => ({ ...prev, height: last.height }))
            }
        } catch (error) {
            toast.error("Erreur chargement mesures")
        } finally {
            setLoading(false)
        }
    }

    const handleSubmit = async (e) => {
        e.preventDefault()
        try {
            const payload = Object.fromEntries(
                Object.entries(formData).map(([k, v]) =>[k, v === '' ? null : v])
            )
            await api.post(`/api/measurements/patient/${patientId}`, payload)
            toast.success("Mesure ajoutée")
            setIsModalOpen(false)
            fetchMeasures()
        } catch (error) {
            toast.error("Erreur sauvegarde")
        }
    }

    const handleDelete = async (id) => {
        if(!confirm("Supprimer cette mesure ?")) return
        try {
            await api.delete(`/api/measurements/${id}`)
            toast.success("Supprimée")
            fetchMeasures()
        } catch (error) {
            toast.error("Erreur")
        }
    }

    // --- CALCULATEURS INTELLIGENTS ---
    const calculateIMC = (weight, height) => {
        if (!weight || !height) return '-'
        const hM = height / 100
        return (weight / (hM * hM)).toFixed(1)
    }

    const getIMCCategory = (weight, height) => {
        if (!weight || !height) return { label: '-', color: 'text-gray-400 bg-gray-50' }
        const hM = height / 100
        const imc = weight / (hM * hM)
        
        // --- NORMES ENFANTS (< 18 ans) ---
        if (patientAge && patientAge < 18) {
            return { label: 'Enfant (Courbe requise)', color: 'bg-indigo-100 text-indigo-800' }
        }
        
        // --- NORMES OMS SENIORS (≥ 65 ans) ---
        if (patientAge && patientAge >= 65) {
            if (imc < 21) return { label: 'Dénutrition', color: 'bg-red-100 text-red-800' }
            if (imc < 22) return { label: 'Surveillance / Risque', color: 'bg-yellow-100 text-yellow-800' }
            if (imc <= 30) return { label: 'Corpulence normale', color: 'bg-green-100 text-green-800' }
            if (imc < 35) return { label: 'Obésité (Grade I)', color: 'bg-orange-100 text-orange-800' }
            if (imc < 40) return { label: 'Obésité sévère (Grade II)', color: 'bg-red-100 text-red-800' }
            return { label: 'Obésité morbide (Grade III)', color: 'bg-red-200 text-red-900' }
        }
        
        // --- NORMES OMS ADULTES (18 à 65 ans) ---
        if (imc < 18.5) return { label: 'Maigreur', color: 'bg-blue-100 text-blue-800' }
        if (imc < 25) return { label: 'Corpulence normale', color: 'bg-green-100 text-green-800' }
        if (imc < 30) return { label: 'Surpoids', color: 'bg-yellow-100 text-yellow-800' }
        if (imc < 35) return { label: 'Obésité de grade I (modérée)', color: 'bg-orange-100 text-orange-800' }
        if (imc < 40) return { label: 'Obésité de grade II (sévère)', color: 'bg-red-100 text-red-800' }
        return { label: 'Obésité de grade III (massive/morbide)', color: 'bg-red-200 text-red-900' }
    }


    // NOUVEAU: Rapport Taille/Hanches (WHR) & Tour de Taille combinés
    const getMorphology = (waist, hips) => {
        if (!waist || !hips || !patientGender) return null
        const ratio = waist / hips
        
        let isAndroid = false
        let isGynoid = false
        
        if (patientGender === 'F') {
            if (waist > 88) isAndroid = true
            if (ratio < 0.80) isGynoid = true
        } else if (patientGender === 'M') {
            if (waist > 102) isAndroid = true
            if (ratio < 0.95) isGynoid = true
        }
        
        if (isAndroid && isGynoid) {
            return { label: 'Mixte', desc: 'Abdominale & Hanches', color: 'bg-orange-50 text-orange-700 border-orange-200', ratio: ratio.toFixed(2) }
        } else if (isAndroid) {
            return { label: 'Androïde', desc: 'Risque Cardio ⬆️ (Abdominale)', color: 'bg-red-50 text-red-700 border-red-200', ratio: ratio.toFixed(2) }
        } else if (isGynoid) {
            return { label: 'Gynoïde', desc: 'Hanches, fesses, cuisses', color: 'bg-green-50 text-green-700 border-green-200', ratio: ratio.toFixed(2) }
        } else {
            return { label: 'Harmonieuse', desc: 'Répartition normale', color: 'bg-gray-50 text-gray-700 border-gray-200', ratio: ratio.toFixed(2) }
        }
    }


    if (loading) return <div className="p-12 text-center"><div className="spinner"/></div>

    return (
        <div className="space-y-8">
            <div className="flex justify-between items-center">
                <h3 className="text-lg font-serif text-sage-700">Suivi Biométrique</h3>
                <button onClick={() => {
                    // On garde la taille de la dernière saisie si possible, et on vide le reste
                    setFormData(prev => ({...prev, date: new Date().toISOString().split('T')[0], weight: '', waist: '', hips: '', fat_mass: '', muscle_mass: ''}))
                    setIsModalOpen(true)
                }} className="btn btn-primary py-2 text-sm flex items-center gap-2">
                    <Plus className="w-4 h-4" /> Ajouter une mesure
                </button>
            </div>

            {/* GRAPHIQUE */}
            {measures.length > 1 ? (
                <div className="bg-white border border-sage-100 rounded-2xl p-6 shadow-sm h-[300px]">
                    <h4 className="text-sm font-bold text-gray-500 uppercase mb-4">Évolution du Poids (kg)</h4>
                    <ResponsiveContainer width="100%" height="100%">
                        {(() => {
                            // On dé-doublonne par date en gardant la dernière mesure de la journée
                            const chartData = Object.values(measures.reduce((acc, m) => {
                                acc[m.date] = m;
                                return acc;
                            }, {}));
                            
                            return (
                                <LineChart data={chartData}>
                                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eee" />
                                    <XAxis dataKey="date" tickFormatter={(str) => format(new Date(str), 'dd/MM')} stroke="#9ca3af" fontSize={12} tickMargin={10}/>
                                    <YAxis domain={['auto', 'auto']} stroke="#9ca3af" fontSize={12} unit=" kg"/>
                                    <Tooltip contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }} labelFormatter={(label) => format(new Date(label), 'dd MMMM yyyy', { locale: fr })}/>
                                    <Line type="monotone" dataKey="weight" stroke="#7d9668" strokeWidth={3} dot={{ fill: '#7d9668', strokeWidth: 2 }} activeDot={{ r: 6 }} name="Poids"/>
                                </LineChart>
                            );
                        })()}
                    </ResponsiveContainer>
                </div>
            ) : (
                <div className="bg-gray-50 border border-dashed border-gray-200 rounded-xl p-8 text-center text-gray-400">
                    <Activity className="w-10 h-10 mx-auto mb-2 opacity-50" />
                    <p>Ajoutez au moins 2 mesures pour voir la courbe d'évolution.</p>
                </div>
            )}

            {/* TABLEAU */}
            <div className="overflow-hidden rounded-xl border border-gray-200 shadow-sm">
                <table className="w-full text-sm text-left">
                    <thead className="bg-gray-50 text-gray-500 font-bold uppercase text-[10px] tracking-wider">
                        <tr>
                            <th className="px-4 py-3">Date</th>
                            <th className="px-4 py-3 text-right">Poids & Taille</th>
                            <th className="px-4 py-3 text-right">IMC & Grade</th>
                            <th className="px-4 py-3 text-right">T. Taille / Hanches</th>
                            <th className="px-4 py-3 text-right">Profil (RTH)</th>
                            <th className="px-4 py-3 text-right">M. Grasse</th>
                            <th className="px-4 py-3 text-center">Actions</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 bg-white">
                        {measures.length === 0 && (
                            <tr><td colSpan="7" className="p-4 text-center text-gray-400 italic">Aucune donnée</td></tr>
                        )}
                        {[...measures].reverse().map((m) => {
                            const morpho = getMorphology(m.waist, m.hips)
                            return (
                            <tr key={m.id} className="hover:bg-sage-50 transition-colors">
                                <td className="px-4 py-3 font-medium text-gray-700">
                                    {format(new Date(m.date), 'dd/MM/yyyy')}
                                </td>
                                <td className="px-4 py-3 text-right">
                                    <div className="font-bold text-sage-700">{m.weight ? `${m.weight} kg` : '-'}</div>
                                    <div className="text-xs text-gray-400">{m.height ? `${m.height} cm` : '-'}</div>
                                </td>
                                <td className="px-4 py-3 text-right">
                                    <div className="flex flex-col items-end gap-1">
                                        <span className="font-bold text-gray-800 text-sm">
                                            {calculateIMC(m.weight, m.height)}
                                        </span>
                                        <span className={`px-2 py-0.5 rounded text-[9px] font-bold tracking-wide uppercase ${getIMCCategory(m.weight, m.height).color}`}>
                                            {getIMCCategory(m.weight, m.height).label}
                                        </span>
                                    </div>
                                </td>
                                <td className="px-4 py-3 text-right text-gray-600 font-medium">
                                    {m.waist || '-'} <span className="text-gray-300">/</span> {m.hips || '-'} cm
                                </td>
                                <td className="px-4 py-3 text-right">
                                    {morpho ? (
                                        <div className="flex flex-col items-end gap-0.5">
                                            <span className={`px-2 py-0.5 border rounded text-[10px] font-bold uppercase tracking-wider ${morpho.color}`}>
                                                {morpho.label} ({morpho.ratio})
                                            </span>
                                            <span className="text-[9px] text-gray-400 font-medium">{morpho.desc}</span>
                                        </div>
                                    ) : (
                                        <span className="text-gray-300">-</span>
                                    )}
                                </td>
                                <td className="px-4 py-3 text-right font-medium text-gray-600">
                                    {m.fat_mass ? `${m.fat_mass} %` : '-'}
                                </td>
                                <td className="px-4 py-3 text-center">
                                    <button onClick={() => handleDelete(m.id)} className="p-1.5 rounded hover:bg-white text-gray-400 hover:text-red-500 border border-transparent hover:border-red-200 transition-colors">
                                        <Trash2 className="w-4 h-4" />
                                    </button>
                                </td>
                            </tr>
                        )})}
                    </tbody>
                </table>
            </div>

            {/* MODALE AJOUT */}
            {isModalOpen && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 backdrop-blur-sm">
                    <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl p-6">
                        <h3 className="text-xl font-serif font-bold text-gray-800 mb-6">Nouvelle mesure</h3>
                        <form onSubmit={handleSubmit} className="space-y-5">
                            <div>
                                <label className="text-xs font-bold text-gray-500 uppercase">Date</label>
                                <input type="date" required value={formData.date} onChange={e => setFormData({...formData, date: e.target.value})} className="w-full mt-1" />
                            </div>
                            
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="text-xs font-bold text-gray-500 uppercase flex items-center gap-1"><Weight className="w-3 h-3"/> Poids (kg)</label>
                                    <input type="number" step="0.1" required value={formData.weight} onChange={e => setFormData({...formData, weight: e.target.value})} className="w-full mt-1 font-bold text-lg text-sage-700 focus:ring-sage-300 border-sage-200" placeholder="0.0" />
                                </div>
                                <div>
                                    <label className="text-xs font-bold text-gray-500 uppercase flex items-center gap-1"><Ruler className="w-3 h-3"/> Taille (cm)</label>
                                    <input type="number" step="0.1" value={formData.height} onChange={e => setFormData({...formData, height: e.target.value})} className="w-full mt-1" placeholder="170" />
                                </div>
                            </div>

                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-4 border-t border-gray-100">
                                <div>
                                    <label className="text-[10px] font-bold text-gray-500 uppercase">Taille (cm)</label>
                                    <input type="number" step="0.1" placeholder="Nombril" value={formData.waist} onChange={e => setFormData({...formData, waist: e.target.value})} className="w-full mt-1 text-sm" />
                                </div>
                                <div>
                                    <label className="text-[10px] font-bold text-gray-500 uppercase">Hanches (cm)</label>
                                    <input type="number" step="0.1" placeholder="Bassin" value={formData.hips} onChange={e => setFormData({...formData, hips: e.target.value})} className="w-full mt-1 text-sm" />
                                </div>
                                <div>
                                    <label className="text-[10px] font-bold text-gray-500 uppercase">Masse Grasse (%)</label>
                                    <input type="number" step="0.1" placeholder="Balance" value={formData.fat_mass} onChange={e => setFormData({...formData, fat_mass: e.target.value})} className="w-full mt-1 text-sm" />
                                </div>
                            </div>

                            <div className="pt-4 flex gap-3">
                                <button type="button" onClick={() => setIsModalOpen(false)} className="flex-1 btn bg-white border border-gray-300 text-gray-600 hover:bg-gray-50">Annuler</button>
                                <button type="submit" className="flex-1 btn btn-primary">Enregistrer</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    )
}